import { useMemo } from 'react';
import {
    ACTIVE_LOSSES_VENDORS,
    normalizeLossesVendor,
    resolveVendorLossesKey,
    vendorLossesValues,
    type ActiveLossesVendor,
} from '../../Constants';
import type { FinancePartnerEligibilitySummary } from '../../components/Layout-Components/Editor-Layout/Finance-Partner-Eligibility/FinancePartnerEligibility';
import { useViewerStore } from '../../store/store';
import {
    applyLossesVendor,
    lossesVendorProductionRatio,
    resolveActiveLossesVendor,
    resolveLossesVendorForPartner,
} from './preContractPartnerLosses';
import { useOneButtonStore } from '../../store/oneButtonStore';
import {
    countIncludedModules,
    estimateSelectedAnnualKwh,
} from './preContractModuleLayout';
import {
    averageIncludedMultiplier,
    resolveLivePartnerQuote,
    type PpwPartnerQuote,
    type PreContractPpwMetricsOverride,
} from './preContractPpwPricing';
import { preContractSystemSizeKw } from './preContractPricing';
import type { PreContractProductPricingOption } from './preContractProductPartners';
import { resolvePreContractEscalator } from './preContractSandboxPricing';
import type { PreContractIntakeForm } from './preContractTypes';

export type PreContractLossesViewportPreview = {
    label: string;
    layouts: PreContractIntakeForm['roofModuleLayouts'];
    roofSelections: boolean[];
    baseMetrics?: PreContractPpwMetricsOverride | null;
    modified?: boolean;
    /** When false, the details bar treats this as the working layout rather than a preview. */
    showPreviewFlags?: boolean;
};

export type PreContractLossesPricingCard = {
    vendor: ActiveLossesVendor;
    label: string;
    /** Finance partner this vendor's losses are priced against, when one is resolvable. */
    partner: string | null;
    eligibility: FinancePartnerEligibilitySummary | null;
    option: PreContractProductPricingOption | null;
    productionKwh: number | null;
    /** Vendor the viewer's per-module production was solved under. */
    sourceVendor: ActiveLossesVendor;
    /** Scale from the vendor the viewer ran to this one, for per-module figures. */
    productionRatio: number;
    quote: PpwPartnerQuote | null;
    lossRows: { lossName: string; percentage: string }[];
    active: boolean;
    /** Partner assignment expects this vendor's losses. */
    expected: boolean;
};

export type PreContractLossesPricingInput = {
    form: PreContractIntakeForm;
    financePartner?: string | null;
    financeOptions?: PreContractProductPricingOption[];
    financePartnerEligibility?: FinancePartnerEligibilitySummary[];
    selectedPricingOption?: PreContractProductPricingOption | null;
    metricsOverride?: PreContractPpwMetricsOverride | null;
    viewportPreview?: PreContractLossesViewportPreview | null;
    onSelectFinanceOption?: (option: PreContractProductPricingOption) => void;
};

function normalizedPartnerLabel(value: string): string {
    return value.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function findPartnerEligibility(
    partner: string | null,
    summaries: FinancePartnerEligibilitySummary[],
): FinancePartnerEligibilitySummary | null {
    const key = normalizedPartnerLabel(partner ?? '');
    if (!key) return null;
    return (
        summaries.find((summary) =>
            [summary.partnerId, summary.partnerName, ...summary.aliases].some((label) => {
                const candidate = normalizedPartnerLabel(label);
                return candidate === key || candidate.includes(key) || key.includes(candidate);
            }),
        ) ?? null
    );
}

export function lossesSourceLabel(vendor: ActiveLossesVendor): string {
    return vendor === 'EnFin' ? 'Enfin losses' : 'oneDRAW losses';
}

/**
 * Shared design + pricing numbers for the active layout, resolved once per losses vendor.
 * The viewer only ever produces production for the vendor it is currently running, so the
 * inactive vendor is scaled by the ratio of the two flat loss stacks.
 */
export function usePreContractLossesPricing({
    form,
    financePartner = null,
    financeOptions = [],
    financePartnerEligibility = [],
    selectedPricingOption = null,
    metricsOverride = null,
    viewportPreview = null,
    onSelectFinanceOption,
}: PreContractLossesPricingInput) {
    const selectedLossesVendor = useViewerStore((state) => state.selectedLossesVendor);
    const systemDesignLosses = useOneButtonStore((state) => state.systemDesignLosses);
    const productionLossesVendor = useViewerStore(
        (state) => state.pciProductionLossesVendor,
    );
    const productionLossesPending = useViewerStore(
        (state) => state.pciProductionLossesPending,
    );
    const lossesVendor = resolveActiveLossesVendor(selectedLossesVendor, systemDesignLosses);

    /**
     * Vendor the viewer's per-module production was actually solved under. Switching
     * the losses tab must not re-label those kWh as the new vendor until shade finishes.
     */
    const sourceVendor: ActiveLossesVendor = productionLossesVendor
        ? normalizeLossesVendor(productionLossesVendor)
        : 'oneDRAW';

    const viewportPreviewActive = viewportPreview != null;
    const layouts = viewportPreviewActive ? viewportPreview.layouts : form.roofModuleLayouts;
    const roofSelections = viewportPreviewActive
        ? viewportPreview.roofSelections
        : form.roofSelections;

    const recordMetrics =
        viewportPreviewActive && !viewportPreview?.modified
            ? viewportPreview?.baseMetrics ?? metricsOverride
            : null;

    const { count: liveModuleCount } = useMemo(
        () => countIncludedModules(layouts, roofSelections),
        [layouts, roofSelections],
    );
    const moduleCount = (() => {
        if (recordMetrics?.moduleCount != null && recordMetrics.moduleCount > 0) {
            return recordMetrics.moduleCount;
        }
        const wattage = form.moduleTypeWattage;
        if (
            recordMetrics?.systemKw != null &&
            recordMetrics.systemKw > 0 &&
            wattage != null &&
            wattage > 0
        ) {
            return Math.round((recordMetrics.systemKw * 1000) / wattage);
        }
        return liveModuleCount;
    })();

    const systemKw = useMemo(() => {
        if (recordMetrics?.systemKw != null && recordMetrics.systemKw > 0) {
            return recordMetrics.systemKw;
        }
        const wattage = form.moduleTypeWattage;
        if (wattage == null || !Number.isFinite(wattage) || wattage <= 0 || moduleCount <= 0) {
            return preContractSystemSizeKw(form);
        }
        return Math.round(((moduleCount * wattage) / 1000) * 1000) / 1000;
    }, [form, moduleCount, recordMetrics]);

    const multiplier = useMemo(() => {
        const live = averageIncludedMultiplier(layouts, roofSelections);
        if (live != null && live > 0) return live;
        if (
            viewportPreviewActive &&
            viewportPreview?.baseMetrics?.multiplier != null &&
            !viewportPreview.modified
        ) {
            return viewportPreview.baseMetrics.multiplier;
        }
        return live;
    }, [layouts, roofSelections, viewportPreview, viewportPreviewActive]);

    /**
     * Production for the modules currently on the board. An applied option's stored figure
     * goes stale the moment the layout is edited, so pricing reads the selection instead.
     */
    const selectedProductionEstimate = useMemo(
        () =>
            estimateSelectedAnnualKwh({
                layouts,
                roofSelections,
                moduleWattage: form.moduleTypeWattage,
            }),
        [form.moduleTypeWattage, layouts, roofSelections],
    );

    const effectiveMetricsOverride = useMemo((): PreContractPpwMetricsOverride | null => {
        if (viewportPreviewActive && viewportPreview?.modified) {
            return {
                systemKw: systemKw > 0 ? systemKw : null,
                annualProductionKwh: selectedProductionEstimate.kwh,
                multiplier: multiplier != null && multiplier > 0 ? multiplier : null,
            };
        }
        if (viewportPreviewActive) {
            return viewportPreview?.baseMetrics ?? metricsOverride;
        }
        return metricsOverride;
    }, [
        metricsOverride,
        multiplier,
        selectedProductionEstimate.kwh,
        systemKw,
        viewportPreview,
        viewportPreviewActive,
    ]);

    /**
     * Design production under the active losses vendor. This deliberately ignores any
     * metrics override so the figure always agrees with the modules on the board.
     */
    const baseProductionKwh = useMemo(() => {
        if (
            recordMetrics?.annualProductionKwh != null &&
            recordMetrics.annualProductionKwh > 0
        ) {
            return recordMetrics.annualProductionKwh;
        }
        const kwh = selectedProductionEstimate.kwh;
        return kwh != null && kwh > 0 ? kwh : null;
    }, [recordMetrics?.annualProductionKwh, selectedProductionEstimate.kwh]);

    const baseMultiplier = useMemo(() => {
        const fromOverride = effectiveMetricsOverride?.multiplier;
        if (fromOverride != null && Number.isFinite(fromOverride) && fromOverride > 0) {
            return fromOverride;
        }
        return multiplier != null && multiplier > 0 ? multiplier : null;
    }, [effectiveMetricsOverride?.multiplier, multiplier]);

    const selectedEscalator = resolvePreContractEscalator(form.targetEscalator);

    const expectedLossesVendor = financePartner
        ? resolveLossesVendorForPartner(financePartner)
        : null;

    const availableFinanceOptions = useMemo(() => {
        const byPartner = new Map<string, PreContractProductPricingOption>();
        for (const option of financeOptions) {
            const partner = option.partner.trim();
            if (partner && option.available && !byPartner.has(partner)) {
                byPartner.set(partner, option);
            }
        }
        return Array.from(byPartner.values());
    }, [financeOptions]);

    const cards = useMemo<PreContractLossesPricingCard[]>(() => {
        const partnerForVendor = (vendor: ActiveLossesVendor): string | null => {
            if (financePartner && resolveLossesVendorForPartner(financePartner) === vendor) {
                return financePartner;
            }
            const match = availableFinanceOptions.find(
                (option) => resolveLossesVendorForPartner(option.partner) === vendor,
            );
            return match?.partner ?? (vendor === 'EnFin' ? 'Enfin' : null);
        };
        return ACTIVE_LOSSES_VENDORS.map((item) => {
            const ratio = lossesVendorProductionRatio(sourceVendor, item.value);
            const partner = partnerForVendor(item.value);
            const productionKwh = baseProductionKwh != null ? baseProductionKwh * ratio : null;
            const quote = resolveLivePartnerQuote(form, partner, {
                escalator: selectedEscalator,
                metricsOverride: {
                    systemKw: systemKw > 0 ? systemKw : null,
                    annualProductionKwh: productionKwh,
                    multiplier: baseMultiplier != null ? baseMultiplier * ratio : null,
                },
            });
            const option =
                availableFinanceOptions.find((entry) => entry.partner === partner) ??
                (partner && partner === selectedPricingOption?.partner
                    ? selectedPricingOption
                    : null);
            return {
                vendor: item.value,
                label: item.label,
                partner,
                eligibility: findPartnerEligibility(partner, financePartnerEligibility),
                option,
                productionKwh,
                sourceVendor,
                productionRatio: ratio,
                quote,
                lossRows: vendorLossesValues[resolveVendorLossesKey(item.value)] ?? [],
                active: item.value === lossesVendor,
                expected: expectedLossesVendor === item.value,
            };
        });
    }, [
        availableFinanceOptions,
        baseMultiplier,
        baseProductionKwh,
        expectedLossesVendor,
        financePartner,
        financePartnerEligibility,
        form,
        lossesVendor,
        selectedEscalator,
        selectedPricingOption,
        sourceVendor,
        systemKw,
    ]);

    /**
     * Production under the losses the design is on, which is what the design details bar
     * reports. Equal to the base once the viewer has re-solved for that vendor.
     */
    const pinSavedProduction =
        recordMetrics?.annualProductionKwh != null && recordMetrics.annualProductionKwh > 0;
    const activeProductionRatio = pinSavedProduction
        ? 1
        : lossesVendorProductionRatio(sourceVendor, lossesVendor);
    const activeProductionKwh =
        baseProductionKwh != null ? baseProductionKwh * activeProductionRatio : null;

    /** Point both the pricing option and the viewer losses source at one vendor. */
    const selectPricing = (vendor: ActiveLossesVendor, partner: string | null) => {
        const option = availableFinanceOptions.find((entry) => entry.partner === partner);
        if (option && option.partner !== financePartner) onSelectFinanceOption?.(option);
        applyLossesVendor(vendor);
    };

    return {
        lossesVendor,
        sourceVendor,
        expectedLossesVendor,
        lossesMismatch: expectedLossesVendor != null && expectedLossesVendor !== lossesVendor,
        cards,
        layouts,
        roofSelections,
        moduleCount,
        systemKw,
        multiplier,
        baseProductionKwh,
        activeProductionKwh,
        activeProductionRatio,
        productionLoading:
            (recordMetrics?.annualProductionKwh == null ||
                recordMetrics.annualProductionKwh <= 0) &&
            (selectedProductionEstimate.waitingForShade ||
                (productionLossesPending != null &&
                    normalizeLossesVendor(productionLossesPending) === lossesVendor)),
        selectedProductionEstimate,
        selectedEscalator,
        availableFinanceOptions,
        selectPricing,
    };
}
